import { useEffect, useRef, useState } from 'react';
import { Pause, Play, Square, SkipForward, Waves } from 'lucide-react';
import { useBreathingTimer } from './useBreathingTimer';
import { fmtClock } from '@/lib/breathing';
import { playChime, unlockChime } from '@/lib/breathChime';
import { saveAmbience, setAmbienceVolume, startAmbience, stopAmbience } from '@/lib/breathAmbience';

const PHASE_COLOR = { inhale: '#7DD3FC', hold: '#A78BFA', exhale: '#65A30D' };
const PHASE_INK = { inhale: 'var(--c-sky-ink)', hold: '#7C3AED', exhale: 'var(--c-green-ink)' };

export const BreathingRun = ({ name, phases, totalCycles, voice, chime, ambience, onFinish }) => {
  const circleRef = useRef(null);
  const [started, setStarted] = useState(false);
  const [vol, setVol] = useState(ambience?.volume ?? 35);
  const sound = ambience?.sound || 'off';
  useEffect(() => () => stopAmbience(), []);
  const onCue = (p, cycle, kind) => {
    if (!voice || kind === 'next') return;
    voice.say(p.label || p.type);
  };
  const t = useBreathingTimer({ phases, totalCycles, circleRef, onCue, onFinish: (r) => { voice?.stop(); stopAmbience(); if (chime) playChime(true); onFinish(r); } });
  const v = t.view;
  const phase = phases[v.idx];
  const lead = started && v.lead > 0;
  const color = lead ? '#FF6FA8' : PHASE_COLOR[phase.type];

  const begin = () => { voice?.unlock(); if (chime) { unlockChime(); playChime(); } startAmbience(sound, vol); setStarted(true); t.start(); };
  const pause = () => { t.pause(); voice?.pause(); stopAmbience(0.4); };
  const resume = () => { startAmbience(sound, vol); t.resume(); };
  const changeVol = (n) => { setVol(n); setAmbienceVolume(n); saveAmbience(sound, n); };

  return (
    <div className="flex flex-col items-center text-center gap-6 py-4" data-testid="breathing-run">
      <div className="font-display font-black text-xl" data-testid="breathing-name">{name}</div>
      <div className="relative w-[min(78vw,340px)] aspect-square flex items-center justify-center">
        <div className="absolute inset-0 rounded-full border border-[var(--c-hair)]" />
        <div ref={circleRef} data-testid="breathing-circle" data-phase={lead || !started ? 'lead' : phase.type}
          className="absolute inset-0 rounded-full will-change-transform"
          style={{ transform: 'scale(0.5)', background: `radial-gradient(circle at 35% 30%, ${color}66, ${color}22 70%)`, boxShadow: `0 0 60px ${color}33`, transition: 'background 600ms ease, box-shadow 600ms ease' }} />
        <div className="relative z-10">
          <div className="text-sm uppercase tracking-[0.2em] font-semibold" style={{ color: lead || !started ? '#DB2777' : PHASE_INK[phase.type] }} data-testid="breathing-instruction">
            {!started ? 'Ready' : lead ? 'Get ready' : (phase.label || phase.type)}
          </div>
          <div className="font-display font-black text-7xl tabular-nums leading-none mt-2" data-testid="breathing-countdown">
            {!started ? '–' : lead ? v.lead : v.countdown}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 w-full max-w-sm text-sm">
        <Stat label="Cycle" testid="breathing-cycle" value={`${Math.min(v.cycle + 1, totalCycles)} / ${totalCycles}`} />
        <Stat label="Elapsed" testid="breathing-elapsed" value={fmtClock(v.elapsed)} />
        <Stat label="Remaining" testid="breathing-remaining" value={fmtClock(v.remaining)} />
      </div>
      <Controls started={started} running={v.running} isHold={started && !lead && phase.type === 'hold'}
        onStart={begin} onPause={pause} onResume={resume} onEnd={t.end} onSkip={t.skipHold} />
      {sound !== 'off' && <AmbienceVolume value={vol} onChange={changeVol} label={sound === 'ocean' ? 'Ocean' : 'Rain'} />}
    </div>
  );
};

export const AmbienceVolume = ({ value, onChange, label }) => (
  <label className="flex items-center gap-3 w-full max-w-xs text-xs text-muted-foreground" data-testid="ambience-volume-control">
    <Waves className="w-4 h-4 flex-shrink-0" /> <span className="w-12 text-left">{label}</span>
    <input type="range" min="0" max="100" step="5" value={value} onChange={e => onChange(+e.target.value)}
      aria-label="Background sound volume" data-testid="ambience-volume" className="flex-1 accent-[#7DD3FC]" />
    <span className="w-8 tabular-nums text-right">{value}%</span>
  </label>
);

const Stat = ({ label, value, testid }) => (
  <div className="regen-card-elevated py-2.5 px-2">
    <div className="label-mini text-muted-foreground">{label}</div>
    <div className="font-display font-bold tabular-nums mt-0.5" data-testid={testid}>{value}</div>
  </div>
);

const Controls = ({ started, running, isHold, onStart, onPause, onResume, onEnd, onSkip }) => {
  const btn = 'h-12 rounded-full px-5 font-semibold inline-flex items-center gap-2 transition-transform active:scale-95';
  if (!started) return <button onClick={onStart} data-testid="breathing-begin" className={`${btn} bg-[#7DD3FC] text-[#081726] px-10`}><Play className="w-5 h-5" /> Begin</button>;
  return (
    <div className="flex flex-wrap items-center justify-center gap-3">
      {running
        ? <button onClick={onPause} data-testid="breathing-pause" className={`${btn} bg-[var(--c-film)]`}><Pause className="w-5 h-5" /> Pause</button>
        : <button onClick={onResume} data-testid="breathing-resume" className={`${btn} bg-[#7DD3FC] text-[#081726]`}><Play className="w-5 h-5" /> Resume</button>}
      {isHold && running && <button onClick={onSkip} data-testid="breathing-skip-hold" className={`${btn} bg-[var(--c-film)]`}><SkipForward className="w-5 h-5" /> Skip hold</button>}
      <button onClick={onEnd} data-testid="breathing-end" className={`${btn} border border-[var(--c-hair)] text-muted-foreground`}><Square className="w-4 h-4" /> End session</button>
    </div>
  );
};
